import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { msalConfig, loginRequest, apiRequest, bypassEnabled } from '@/config/auth';
import { API_CONFIG } from '@/config/api';

interface User {
  email: string;
  name?: string;
  roles: string[];  // ['admin', 'user']
}

interface AuthContextType {
  isAuthenticated: boolean;
  isBypassAuth: boolean;
  user: User | null;
  isAdmin: boolean;
  isUser: boolean;
  login: (password?: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  authMethod: 'msal' | 'bypass' | null;
  inProgress: InteractionStatus;
  isLoadingUser: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export context for use in hook
export { AuthContext };

const msalInstance = new PublicClientApplication(msalConfig);

const AuthProviderInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [bypassPassword, setBypassPassword] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authMethod, setAuthMethod] = useState<'msal' | 'bypass' | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);

  // Define getToken first (no dependencies on other functions)
  const getToken = useCallback(async (): Promise<string | null> => {
    // CRITICAL: Check bypass mode FIRST before trying MSAL
    // This prevents MSAL errors when using bypass authentication
    if (authMethod === 'bypass' && bypassPassword) {
      return bypassPassword;
    }

    // Only attempt MSAL if we're explicitly in MSAL mode
    if (authMethod === 'msal' && accounts.length > 0) {
      try {
        const response = await instance.acquireTokenSilent({
          ...apiRequest,
          account: accounts[0]
        });
        return response.accessToken;
      } catch (error) {
        try {
          const response = await instance.acquireTokenRedirect(apiRequest);
          return response.accessToken;
        } catch (redirectError) {
          console.error('Token acquisition failed:', redirectError);
          throw redirectError;
        }
      }
    }

    return null;
  }, [instance, accounts, bypassPassword, authMethod]);

  // Define fetchUserInfo (depends on getToken)
  const fetchUserInfo = useCallback(async (token: string | null, method: 'msal' | 'bypass') => {
    setIsLoadingUser(true);
    try {
      // For bypass auth, use the provided token directly
      // For MSAL, get token if not provided
      let authToken = token;
      if (!authToken && method === 'msal') {
        authToken = await getToken();
      }

      if (!authToken) {
        // Silently return - this happens during initial page load before token is ready
        return;
      }

      console.log('[AuthContext] Fetching user info from /api/auth/me with method:', method);
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[AuthContext] Received user data:', data);
        setUser({
          email: data.email,
          name: data.name,
          roles: data.roles || []
        });
      } else {
        console.error('[AuthContext] Failed to fetch user info:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('[AuthContext] Error response:', errorText);
      }
    } catch (error) {
      console.error('[AuthContext] Failed to fetch user info:', error);
    } finally {
      setIsLoadingUser(false);
    }
  }, [getToken]);

  // Check for stored bypass password
  useEffect(() => {
    const stored = sessionStorage.getItem('bypass_password');
    if (stored) {
      setBypassPassword(stored);
      setAuthMethod('bypass');
      fetchUserInfo(stored, 'bypass');
    }
  }, [fetchUserInfo]);

  // Handle MSAL authentication - only if not using bypass
  useEffect(() => {
    // Skip MSAL if we're using bypass authentication
    if (bypassPassword || authMethod === 'bypass') {
      return;
    }

    if (isAuthenticated && accounts.length > 0) {
      setAuthMethod('msal');
      fetchUserInfo(null, 'msal');
    }
  }, [isAuthenticated, accounts, bypassPassword, authMethod, fetchUserInfo]);

  const login = async (password?: string) => {
    if (password) {
      // Password login - set bypass
      setBypassPassword(password);
      sessionStorage.setItem('bypass_password', password);
      setAuthMethod('bypass');
      await fetchUserInfo(password, 'bypass');
      return;
    }

    // Microsoft login - use redirect instead of popup for better reliability
    try {
      await instance.loginRedirect(loginRequest);
      // authMethod will be set to 'msal' by the useEffect when isAuthenticated becomes true
    } catch (error) {
      console.error('Microsoft login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    if (authMethod === 'bypass') {
      setBypassPassword(null);
      sessionStorage.removeItem('bypass_password');
      setUser(null);
      setAuthMethod(null);
    } else if (accounts.length > 0) {
      await instance.logoutRedirect();
    }
  };

  // Helper properties for role checking
  const isAdmin = user?.roles?.includes('admin') || false;
  const isUser = user?.roles?.includes('user') || isAdmin; // Admin implies User

  const contextValue: AuthContextType = {
    isAuthenticated: !!user && (isAuthenticated || !!bypassPassword),
    isBypassAuth: authMethod === 'bypass',
    user,
    isAdmin,
    isUser,
    login,
    logout,
    getToken,
    authMethod,
    inProgress,
    isLoadingUser
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </MsalProvider>
  );
};